/**
 * The "i" beside a measure's name, and the card it opens: the measure's name, one or
 * two plain sentences, and — where the short answer leaves something out — a link to
 * the full entry on Data & method.
 *
 * It replaced a native `title` tooltip (2026-09-24): that one could not be styled,
 * took a second to appear, never appeared on touch, and could not hold a link.
 *
 * The card is `position: fixed` but stays in the DOM right after the button rather
 * than in a portal. Fixed positioning is what lets it escape the table cards that clip
 * their overflow; staying in place is what lets Tab move from the button into its link.
 * Being fixed, it has to be re-placed when the page scrolls — closing it on scroll
 * instead shut it the moment keyboard focus scrolled an "i" into view.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type React from 'react'
import { Link } from 'react-router-dom'
import type { Definition } from '@/measures/definitions'
import { describeWindow } from '@/measures/collection'
import { useCollectionWindow } from '@/data/CollectionWindowContext'

const CARD_WIDTH = 288
const GAP = 8

/**
 * The hover card's mechanics, shared by the "i" and the score breakdown
 * (components/ScoreTip.tsx): opens on hover and focus, closes after a short grace on
 * leaving, on blur and on Escape, and follows its trigger on scroll and resize.
 */
export function useHoverCard(width: number) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean; maxH: number } | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const card = useRef<HTMLSpanElement>(null)
  const closing = useRef<number | undefined>(undefined)
  const id = useId()

  const cancelClose = () => window.clearTimeout(closing.current)
  const show = () => {
    cancelClose()
    setOpen(true)
  }
  // A short grace period, so the pointer can cross the gap from the trigger to the card.
  const hide = () => {
    cancelClose()
    closing.current = window.setTimeout(() => setOpen(false), 140)
  }

  /** Centred on the trigger, kept on screen, flipped above when there is more room there. */
  const place = () => {
    if (!btn.current) return
    const r = btn.current.getBoundingClientRect()
    // scrollHeight: the full card, not the part a max-height left showing.
    const h = card.current?.scrollHeight ?? 140
    const left = Math.min(
      Math.max(GAP, r.left + r.width / 2 - width / 2),
      window.innerWidth - width - GAP,
    )
    // Below unless it does not fit there and there is more room above. A card taller
    // than either side scrolls inside rather than running off screen.
    const roomBelow = window.innerHeight - r.bottom - GAP
    const roomAbove = r.top - GAP
    const above = h > roomBelow && roomAbove > roomBelow
    setPos({
      left,
      top: above ? Math.max(GAP, r.top - GAP - h) : r.bottom + GAP,
      above,
      maxH: Math.max(120, (above ? roomAbove : roomBelow) - GAP),
    })
  }

  // Once it exists and can be measured.
  useLayoutEffect(() => {
    if (open) place()
  }, [open])

  // Follow the trigger on scroll and resize, one placement per frame; Escape closes.
  useEffect(() => {
    if (!open) return
    let frame = 0
    const follow = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        btn.current?.focus()
      }
    }
    window.addEventListener('scroll', follow, true)
    window.addEventListener('resize', follow)
    document.addEventListener('keydown', onKey)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', follow, true)
      window.removeEventListener('resize', follow)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => cancelClose, [])

  /** Spread on the wrapper around the trigger and the card. */
  const wrapper = {
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hide()
    },
  }
  /** Spread on the card. */
  const cardProps = {
    ref: card,
    id,
    role: 'tooltip' as const,
    className: `info-card${pos?.above ? ' is-above' : ''}`,
    style: { width, left: pos?.left ?? -9999, top: pos?.top ?? -9999, maxHeight: pos?.maxH },
  }

  return { open, setOpen, show, id, btn, wrapper, cardProps }
}

export function Info({ def }: { def: Definition | undefined }) {
  const { open, setOpen, show, id, btn, wrapper, cardProps } = useHoverCard(CARD_WIDTH)
  const collWindow = useCollectionWindow()

  if (!def) return null

  return (
    <span
      className="info"
      {...wrapper}
      // The "i" often sits inside a clickable row or a sortable header.
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={btn}
        type="button"
        className="info-dot"
        aria-label={`What is ${def.label}?`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        // Opens only. Focus has already opened it by the time a click lands, so a toggle
        // here shut it on every click and every tap. Leaving, blur and Escape close it.
        onClick={show}
      >
        i
      </button>
      {open && (
        <span {...cardProps}>
          <span className="info-card-title">{def.label}</span>
          <span className="info-card-text">{def.text}</span>
          {def.window && collWindow && (
            <span className="info-card-text">This range reads {describeWindow(collWindow)}.</span>
          )}
          {def.more && (
            <Link
              to={`/method#${def.more}`}
              className="info-card-more"
              onClick={() => setOpen(false)}
            >
              How it’s calculated →
            </Link>
          )}
        </span>
      )}
    </span>
  )
}
